import {
  IElements,
  IFootballConfig,
  IMostDecoratedConfig,
  IMostDecoratedDataLabelsKeys,
  IMostDecoratedPlayersResponse,
} from '../../../../packages/types';
import { getLabelValue, removeUndefinedPropertiesFromObject } from '../../../../packages/utils';
import { INormalizedMostDecoratedPlayersData } from '../types';
import { normalizeMostDecoratedPlayer } from './normalizeMostDecoratedPlayer';

enum ColumnTypes {
  NAME = 'Name',
  RC = 'RC',
  RED_CARDS = 'Red cards',
  TC = 'TC',
  TOTAL_CARDS = 'Total cards',
  YC = 'YC',
  YELLOW_CARDS = 'Yellow cards',
  FYC = 'FYC',
  FIRST_YELLOW_CARDS = 'First yellow cards',
}

interface INormalizeMostDecoratedPlayersDataArgs {
  mostDecoratedPlayers: IMostDecoratedPlayersResponse[];
  dataEntityLinks?: IFootballConfig['dataEntityLinks'];
  dataLabels?: IMostDecoratedConfig['dataLabels'];
  dataHighlightEntities?: IMostDecoratedConfig['dataHighlightEntities'];
  dataTeamNameType?: IMostDecoratedConfig['dataTeamNameType'];
  dataElements?: IElements;
  displayImage?: IMostDecoratedConfig['dataImageDisplay'];
}

const ALLOWED_MOST_DECORATED_ELEMENTS: string[] = [
  IMostDecoratedDataLabelsKeys.FIRST_YELLOW_CARDS,
  IMostDecoratedDataLabelsKeys.RED_CARDS,
  IMostDecoratedDataLabelsKeys.TOTAL_CARDS,
  IMostDecoratedDataLabelsKeys.YELLOW_CARDS,
];

const columnIndicesPerElements: Record<string, number> = {
  [IMostDecoratedDataLabelsKeys.FIRST_YELLOW_CARDS]: 1,
  [IMostDecoratedDataLabelsKeys.RED_CARDS]: 2,
  [IMostDecoratedDataLabelsKeys.TOTAL_CARDS]: 3,
  [IMostDecoratedDataLabelsKeys.YELLOW_CARDS]: 4,
};

export const normalizeMostDecoratedPlayers = ({
  mostDecoratedPlayers,
  dataEntityLinks,
  dataLabels,
  dataHighlightEntities,
  dataTeamNameType,
  dataElements = [],
  displayImage,
}: INormalizeMostDecoratedPlayersDataArgs): INormalizedMostDecoratedPlayersData | undefined => {
  const elements = (Array.isArray(dataElements) ? dataElements : (dataElements.stats as string[]) ?? []).filter((element) =>
    ALLOWED_MOST_DECORATED_ELEMENTS.includes(element),
  );

  const normalizedMostDecoratedPlayer = mostDecoratedPlayers.map((item: IMostDecoratedPlayersResponse) =>
    normalizeMostDecoratedPlayer({
      item,
      dataEntityLinks,
      dataHighlightEntities,
      dataTeamNameType,
      dataElements: elements,
      displayImage,
    }),
  );

  const defaultColumns = [
    { value: getLabelValue(dataLabels?.name, ColumnTypes.NAME), css: { flexGrow: '1' } },
    {
      value: getLabelValue(dataLabels?.first_yellow_cards_short, ColumnTypes.FYC),
      tooltip: getLabelValue(dataLabels?.first_yellow_cards, ColumnTypes.FIRST_YELLOW_CARDS),
    },
    {
      value: getLabelValue(dataLabels?.red_cards_short, ColumnTypes.RC),
      tooltip: getLabelValue(dataLabels?.red_cards, ColumnTypes.RED_CARDS),
    },
    {
      value: getLabelValue(dataLabels?.total_cards_short, ColumnTypes.TC),
      tooltip: getLabelValue(dataLabels?.total_cards, ColumnTypes.TOTAL_CARDS),
    },
    {
      value: getLabelValue(dataLabels?.yellow_cards_short, ColumnTypes.YC),
      tooltip: getLabelValue(dataLabels?.yellow_cards, ColumnTypes.YELLOW_CARDS),
    },
  ];

  const columns: typeof defaultColumns = [
    {
      value: getLabelValue(dataLabels?.name, ColumnTypes.NAME),
      css: { flexGrow: '1' },
    },
  ];

  elements.forEach((element) => {
    const defaultColumn = defaultColumns[columnIndicesPerElements[element]];

    defaultColumn && columns.push(defaultColumn);
  });

  return removeUndefinedPropertiesFromObject({
    items: {
      data: [
        {
          item: {
            data: normalizedMostDecoratedPlayer,
            desktopColumns: elements.length === 0 ? defaultColumns : columns,
            mobileColumns: elements.length === 0 ? defaultColumns : columns,
          },
        },
      ],
    },
  });
};
